import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a date string from TMDB (YYYY-MM-DD format) as a local date.
 * This prevents timezone issues where dates appear 1 day off.
 *
 * When you do `new Date("2026-02-03")`, JavaScript interprets it as midnight UTC,
 * which can appear as the previous day in timezones behind UTC.
 *
 * This function parses the date components and creates a local date at noon,
 * ensuring the correct date is displayed regardless of timezone.
 */
export function parseTMDBDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  // Parse the date parts
  const parts = dateString.split('-');
  if (parts.length !== 3) return new Date(dateString);

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
  const day = parseInt(parts[2], 10);

  // Create date at noon local time to avoid timezone rollover
  return new Date(year, month, day, 12, 0, 0);
}

/**
 * Safely format a TMDB date string. Returns `fallback` (default "TBA") when the
 * date is missing or unparseable — avoids crashes from formatting a null date.
 */
export function formatTMDBDate(
  dateString: string | null | undefined,
  formatStr: string,
  fallback: string = "TBA"
): string {
  const date = parseTMDBDate(dateString);
  if (!date || isNaN(date.getTime())) return fallback;
  return format(date, formatStr);
}
